import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MAX_FIELDS_PER_WORKSPACE } from '@leadgab/shared';
import { PageShell, Section } from '../components/PageShell.tsx';
import { useConfirm } from '../components/ConfirmDialog.tsx';
import { EmptyState, Spinner, formatRelative } from '../components/ui.tsx';
import {
  BellIcon,
  BoxIcon,
  CheckIcon,
  ClipboardIcon,
  CodeIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  TrashIcon,
} from '../components/icons.tsx';
import { ApiDocs } from '../components/inventory/ApiDocs.tsx';
import { FieldDialog } from '../components/inventory/FieldDialog.tsx';
import type { FieldDraft } from '../components/inventory/FieldDialog.tsx';
import { ItemDialog } from '../components/inventory/ItemDialog.tsx';
import { api, ApiRequestError } from '../lib/api.ts';
import { useTabRoute } from '../lib/useTabRoute.ts';
import { cellText } from '../lib/inventory.ts';
import type {
  CellValue,
  FieldRow,
  FieldUsage,
  InventorySettings,
  ItemRow,
  RuleRow,
} from '../lib/inventory.ts';
import { useAuth } from '../lib/auth.tsx';

const TABS = ['items', 'fields', 'requests', 'api'] as const;
type Tab = (typeof TABS)[number];

interface KeyRow {
  id: number;
  uuid: string;
  name: string;
  prefix: string;
  createdBy: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

/**
 * "a product" / "an offcut" — the label is the workspace's own word, so the
 * article cannot be written into the copy. Wrong for the handful of words where
 * spelling and sound disagree ("an hour"), right for anything an inventory is
 * likely to be called.
 */
function article(word: string): string {
  return /^[aeiou]/i.test(word) ? 'an' : 'a';
}

/**
 * Names a few of the fields the box actually covers, because "Search products"
 * says nothing about whether a VIN will work.
 */
function searchHint(fields: FieldRow[], label: string): string {
  const names = fields
    .filter((field) => field.searchable)
    .slice(0, 3)
    .map((field) => field.label.toLowerCase());

  return names.length > 0
    ? `Search by ${names.join(', ')} and more`
    : `Search ${label.toLowerCase()}`;
}

function plural(count: number, noun: string): string {
  return `${String(count)} ${noun}${count === 1 ? '' : 's'}`;
}

interface FieldsResponse {
  items: FieldRow[];
  settings: InventorySettings;
}

/** The workspace's own schema, needed by all three tabs. */
function useFields() {
  return useQuery({
    queryKey: ['inventory', 'fields'],
    queryFn: () => api.get<FieldsResponse>('/inventory/fields'),
  });
}

function errorsOf(error: unknown): { message?: string; fieldErrors: Record<string, string> } {
  if (error instanceof ApiRequestError) {
    return { message: error.message, fieldErrors: error.fieldErrors };
  }
  return { fieldErrors: {} };
}

export function InventoryPage(): React.JSX.Element {
  const [tab, setTab] = useTabRoute<Tab>('/inventory', TABS, 'items');
  const fields = useFields();

  const label = fields.data?.settings.label ?? 'Products';
  const singular = fields.data?.settings.labelSingular ?? 'Product';

  return (
    <PageShell
      title="Inventory"
      description={`What you have in stock, and the customers waiting for it.`}
      tabs={[
        {
          id: 'items',
          label,
          icon: BoxIcon,
          help: {
            summary: `What you have in stock. Adding ${article(singular)} ${singular.toLowerCase()} immediately notifies anyone whose standing request it matches.`,
            points: [
              'Fields are yours to define — this list has exactly the columns your workspace declared.',
              'The search box covers every searchable field at once, so there is nothing to pick first.',
              'An external id lets you re-run a feed: rows are matched on it rather than duplicated.',
              `Clearing hides ${label.toLowerCase()} rather than destroying them, so a mistaken wipe is recoverable.`,
            ],
          },
        },
        {
          id: 'fields',
          label: 'Fields',
          icon: SettingsIcon,
          help: {
            summary: 'The shape of your inventory. Whatever you declare here becomes a column in the list and an input on the add form.',
            points: [
              'A key is set once. It is what stored items and standing requests are keyed on, so renaming edits the label instead.',
              'One box searches every field marked Searchable at once — untick that on a field to keep it out of results.',
              'Deleting a field removes its values everywhere, and takes any standing request that matched only on it — the dialog says how many before you commit.',
            ],
          },
        },
        {
          id: 'requests',
          label: 'Standing requests',
          icon: BellIcon,
          help: {
            summary: 'Customers waiting on stock that has not arrived yet.',
            points: [
              `Each customer hears about a given ${singular.toLowerCase()} once, even if the hourly sweep re-runs.`,
              'The alert is posted into their conversation, and emailed too if they left an address.',
              'A request with no criteria matches everything — that is allowed, and deliberate.',
            ],
          },
        },
        {
          id: 'api',
          label: 'API',
          icon: CodeIcon,
          help: {
            summary: `Fill and clear your ${label.toLowerCase()} from a script, with no browser involved.`,
            points: [
              'A key is shown once, when you create it. Nothing here can show it to you again — copy it then.',
              'Keys reach the inventory and nothing else: not your conversations, not your team, not your standing requests.',
              'Every example below is built from your own fields, so it is ready to paste rather than a template to adapt.',
            ],
          },
        },
      ]}
      active={tab}
      onChange={setTab}
    >
      {fields.isLoading ? (
        <Spinner className="h-5 w-5 text-brand-600" />
      ) : tab === 'items' ? (
        <ItemsTab fields={fields.data?.items ?? []} settings={fields.data?.settings} />
      ) : tab === 'fields' ? (
        <FieldsTab fields={fields.data?.items ?? []} />
      ) : tab === 'api' ? (
        <ApiTab fields={fields.data?.items ?? []} settings={fields.data?.settings} />
      ) : (
        <RequestsTab />
      )}
    </PageShell>
  );
}

// --- items ------------------------------------------------------------------

function ItemsTab({
  fields,
  settings,
}: {
  fields: FieldRow[];
  settings?: InventorySettings;
}): React.JSX.Element {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { isManager } = useAuth();

  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<{ open: boolean; item: ItemRow | null }>({
    open: false,
    item: null,
  });
  const [notified, setNotified] = useState<number | null>(null);

  const label = settings?.label ?? 'Products';
  const singular = settings?.labelSingular ?? 'Product';
  const live = useMemo(() => fields.filter((field) => !field.archived), [fields]);

  // The first column is the item's name, not whichever field the owner happened
  // to declare first — a list whose headline column reads "2015" is not a list
  // you can scan. The name is derived server-side and is the same string the
  // alert messages use.
  //
  // A nominated title field is dropped from the columns after it: it is already
  // the headline, and showing it twice is just a narrower table. With no field
  // nominated the name is a join of several, so nothing is dropped.
  const titleKey = live.find((field) => field.isTitle)?.key;
  const body = live.filter((field) => field.key !== titleKey);

  // The next few become columns; the remainder is summarised under the name, so
  // a workspace with 25 fields still has a list that fits on screen.
  const columns = body.slice(0, 4);
  const rest = body.slice(4);

  // One box over the whole row rather than a column of per-field ones. The
  // server already keeps a flattened, lowercased copy of every searchable value
  // on each item, so a single query hits all of them at once — and the boxes it
  // replaces could never cover more than the first few fields anyway.
  //
  // The per-field `f.<key>=` filters stay on the API for callers driving it
  // directly; they are just not what this screen offers.
  const query = useMemo(() => (search ? { search } : {}), [search]);

  const items = useQuery({
    queryKey: ['inventory', 'items', query],
    queryFn: () => api.get<{ items: ItemRow[] }>('/inventory/items', query),
  });

  const save = useMutation({
    // Only a create reports how many customers it woke up; an edit says nothing,
    // which is what `null` means here rather than "nobody matched".
    mutationFn: async (input: {
      externalId: string | null;
      data: Record<string, CellValue>;
    }): Promise<number | null> => {
      if (dialog.item) {
        await api.patch<ItemRow>(`/inventory/items/${String(dialog.item.id)}`, input);
        return null;
      }

      const created = await api.post<ItemRow & { notified: number }>('/inventory/items', input);
      return created.notified;
    },
    onSuccess: (result) => {
      setDialog({ open: false, item: null });
      setNotified(result);
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/inventory/items/${String(id)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
  });

  const clear = useMutation({
    mutationFn: () => api.post<{ cleared: number }>('/inventory/items/clear', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
  });

  const rows = items.data?.items ?? [];
  const dialogErrors = errorsOf(save.error);

  return (
    <>
      {notified !== null ? (
        <div className="rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">
          {singular} added.{' '}
          {notified > 0
            ? `${String(notified)} waiting ${notified === 1 ? 'customer was' : 'customers were'} notified.`
            : 'No standing requests matched it.'}
        </div>
      ) : null}

      <Section
        title={label}
        description={`Adding ${article(singular)} ${singular.toLowerCase()} immediately notifies anyone whose request it matches.`}
        actions={
          isManager ? (
            <div className="flex gap-2">
              {rows.length > 0 ? (
                <button
                  type="button"
                  className="lg-btn-ghost"
                  onClick={() => {
                    void confirm({
                      title: `Clear all ${label.toLowerCase()}?`,
                      message:
                        'They are hidden rather than destroyed, so this can be undone one row at a time — but the list will look empty straight away.',
                      confirmLabel: 'Clear',
                      destructive: true,
                    }).then((ok) => {
                      if (ok) clear.mutate();
                    });
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                  Clear
                </button>
              ) : null}

              <button
                type="button"
                className="lg-btn-secondary"
                onClick={() => {
                  setDialog({ open: true, item: null });
                }}
              >
                <PlusIcon className="h-4 w-4" />
                Add {singular.toLowerCase()}
              </button>
            </div>
          ) : null
        }
      >
        <div className="relative mb-4">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            className="lg-input pl-9"
            placeholder={searchHint(live, label)}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
        </div>

        {live.length === 0 ? (
          <EmptyState
            icon={<SettingsIcon className="h-10 w-10" />}
            title="No fields yet"
            description="Your inventory has no shape yet. Declare a field or two on the Fields tab and this list will follow."
          />
        ) : items.isLoading ? (
          <Spinner className="h-5 w-5 text-brand-600" />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<BoxIcon className="h-10 w-10" />}
            title={search ? 'No matches' : `No ${label.toLowerCase()} yet`}
            description={
              search
                ? 'Try a different search.'
                : `Add your first ${singular.toLowerCase()} to start matching requests.`
            }
          />
        ) : (
          <div className="lg-scroll -mx-5 overflow-x-auto">
            <table className="w-full min-w-[44rem] text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <th className="px-5 py-2 font-medium whitespace-nowrap">{singular}</th>
                  {columns.map((field) => (
                    <th key={field.key} className="px-3 py-2 font-medium whitespace-nowrap">
                      {field.label}
                    </th>
                  ))}
                  <th className="px-3 py-2 font-medium">Added</th>
                  <th className="px-5 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {rows.map((item) => (
                  <tr key={item.id}>
                    <td className="px-5 py-2.5 font-medium text-slate-800 dark:text-slate-100">
                      {item.title || item.externalId || '—'}

                      {rest.length > 0 || item.externalId ? (
                        <span className="mt-0.5 block text-xs font-normal text-slate-500 dark:text-slate-400">
                          {[
                            ...rest.map((field) => cellText(item.data[field.key])),
                            // Only worth repeating when it is not already the
                            // headline, which it is for a row with nothing else
                            // filled in.
                            item.title ? item.externalId : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      ) : null}
                    </td>

                    {columns.map((field) => (
                      <td key={field.key} className="px-3 py-2.5 text-slate-600 dark:text-slate-300">
                        {cellText(item.data[field.key]) || '—'}
                      </td>
                    ))}

                    <td className="px-3 py-2.5 text-slate-500 dark:text-slate-400">
                      {formatRelative(item.createdAt)}
                    </td>

                    <td className="px-5 py-2.5 text-right whitespace-nowrap">
                      {isManager ? (
                        <>
                          <button
                            type="button"
                            className="lg-btn-ghost px-2"
                            onClick={() => {
                              setDialog({ open: true, item });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="lg-btn-ghost px-2 hover:text-rose-600"
                            onClick={() => {
                              void confirm({
                                title: `Remove this ${singular.toLowerCase()}?`,
                                message:
                                  'It stops matching standing requests. Alerts already sent are unaffected.',
                                confirmLabel: 'Remove',
                                destructive: true,
                              }).then((ok) => {
                                if (ok) remove.mutate(item.id);
                              });
                            }}
                          >
                            <TrashIcon className="h-4 w-4" />
                            <span className="sr-only">Remove</span>
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <ItemDialog
        open={dialog.open}
        fields={fields}
        item={dialog.item}
        labelSingular={singular}
        saving={save.isPending}
        error={dialogErrors.message}
        fieldErrors={dialogErrors.fieldErrors}
        onClose={() => {
          setDialog({ open: false, item: null });
          save.reset();
        }}
        onSubmit={(input) => {
          save.mutate(input);
        }}
      />
    </>
  );
}

// --- fields -----------------------------------------------------------------

function FieldsTab({ fields }: { fields: FieldRow[] }): React.JSX.Element {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { isManager } = useAuth();

  const [dialog, setDialog] = useState<{ open: boolean; field: FieldRow | null }>({
    open: false,
    field: null,
  });
  const [note, setNote] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (draft: FieldDraft) => {
      const options = draft.options
        .split(',')
        .map((option) => option.trim())
        .filter(Boolean);

      const body = {
        label: draft.label,
        required: draft.required,
        searchable: draft.searchable,
        filterable: draft.filterable,
        isTitle: draft.isTitle,
        options,
      };

      return dialog.field
        ? api.patch<FieldRow>(`/inventory/fields/${String(dialog.field.id)}`, body)
        : api.post<FieldRow>('/inventory/fields', { ...body, key: draft.key, kind: draft.kind });
    },
    onSuccess: () => {
      setDialog({ open: false, field: null });
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      api.delete<{ rewrittenItems: number; deletedRules: number; widenedRules: number }>(
        `/inventory/fields/${String(id)}`,
      ),
    onError: (error) => {
      // A destructive action that fails must say so. Silence here reads as
      // success, and the row not disappearing reads as a rendering glitch.
      setNote(
        error instanceof ApiRequestError
          ? `Could not delete the field: ${error.message}`
          : 'Could not delete the field.',
      );
    },
    onSuccess: (result) => {
      // Reported after the fact as well as before it: the confirm said what
      // would happen, this says what did.
      const parts = [
        result.rewrittenItems > 0 ? `${plural(result.rewrittenItems, 'item')} updated` : null,
        result.deletedRules > 0
          ? `${plural(result.deletedRules, 'standing request')} removed`
          : null,
        result.widenedRules > 0
          ? `${plural(result.widenedRules, 'standing request')} now broader`
          : null,
      ].filter(Boolean);

      setNote(parts.length > 0 ? `Field deleted — ${parts.join(', ')}.` : 'Field deleted.');
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  /** Spells out what the delete will cost before it happens. */
  const deleteMessage = (usage: FieldUsage | undefined): string => {
    const lines = ['Its values are removed from every item that had one.'];
    const orphans = usage?.orphans ?? 0;
    const widened = (usage?.rules ?? 0) - orphans;

    if (orphans > 0) {
      lines.push(
        `${plural(orphans, 'standing request')} ${orphans === 1 ? 'matches' : 'match'} only on this field and will be deleted too — without it ${orphans === 1 ? 'it would match' : 'they would match'} every new arrival, and ${orphans === 1 ? 'that customer' : 'those customers'} would be told about all of it.`,
      );
    }

    if (widened > 0) {
      lines.push(`${plural(widened, 'other standing request')} will keep working, but match more widely.`);
    }

    return lines.join(' ');
  };

  /**
   * Counts fresh from the server, not from whenever the page last loaded.
   *
   * A customer can leave a standing request at any moment. A dialog that says
   * "nothing depends on this" because the tab has been open since this morning
   * is worse than one that says nothing at all.
   */
  const confirmDelete = async (field: FieldRow): Promise<void> => {
    let usage = field.usage;

    try {
      const fresh = await api.get<FieldsResponse>('/inventory/fields');
      usage = fresh.items.find((other) => other.id === field.id)?.usage ?? usage;
    } catch {
      // Fall back to what the list already had; the server recomputes inside
      // the transaction either way, so the dialog is advice, not the decision.
    }

    const ok = await confirm({
      title: `Delete “${field.label}”?`,
      message: deleteMessage(usage),
      confirmLabel: 'Delete',
      destructive: true,
    });

    if (ok) remove.mutate(field.id);
  };

  const restore = useMutation({
    mutationFn: (id: number) => api.patch(`/inventory/fields/${String(id)}`, { archived: false }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  const dialogErrors = errorsOf(save.error);

  return (
    <>
      {note ? (
        <div className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
          {note}
        </div>
      ) : null}

      <Section
        title="Fields"
        description="What an item in this inventory is made of. Add whatever your workspace needs — cars, books, machine parts, anything."
        actions={
          isManager ? (
            <button
              type="button"
              className="lg-btn-secondary"
              disabled={fields.length >= MAX_FIELDS_PER_WORKSPACE}
              onClick={() => {
                setDialog({ open: true, field: null });
              }}
            >
              <PlusIcon className="h-4 w-4" />
              Add field
            </button>
          ) : null
        }
      >
        {fields.length === 0 ? (
          <EmptyState
            icon={<SettingsIcon className="h-10 w-10" />}
            title="No fields yet"
            description="Start with the two or three things you would look an item up by — a name, a code, a category."
          />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {fields.map((field) => (
              <li key={field.id} className="flex items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-50">
                    {field.label}
                    <span className="ml-2 font-mono text-xs font-normal text-slate-400">
                      {field.key}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {[
                      field.kind,
                      `${String(field.width)}% wide`,
                      field.required ? 'required' : null,
                      field.filterable ? 'filterable' : null,
                      field.searchable ? 'searchable' : null,
                      field.isTitle ? 'title' : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>

                {field.archived ? (
                  <span className="lg-badge bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    Retired
                  </span>
                ) : null}

                {/* Delete is offered whatever state the field is in. A retired
                    field that could only be restored was a field with no way
                    out of the list at all. */}
                {isManager ? (
                  <>
                    <button
                      type="button"
                      className="lg-btn-ghost px-2"
                      onClick={() => {
                        if (field.archived) restore.mutate(field.id);
                        else setDialog({ open: true, field });
                      }}
                    >
                      {field.archived ? 'Restore' : 'Edit'}
                    </button>

                    <button
                      type="button"
                      className="lg-btn-ghost px-2 hover:text-rose-600"
                      onClick={() => {
                        void confirmDelete(field);
                      }}
                    >
                      <TrashIcon className="h-4 w-4" />
                      <span className="sr-only">Delete</span>
                    </button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <FieldDialog
        open={dialog.open}
        field={dialog.field}
        saving={save.isPending}
        error={dialogErrors.message}
        fieldErrors={dialogErrors.fieldErrors}
        onClose={() => {
          setDialog({ open: false, field: null });
          save.reset();
        }}
        onSubmit={(draft) => {
          save.mutate(draft);
        }}
      />
    </>
  );
}

// --- api --------------------------------------------------------------------

function ApiTab({
  fields,
  settings,
}: {
  fields: FieldRow[];
  settings?: InventorySettings;
}): React.JSX.Element {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { isManager } = useAuth();

  const [name, setName] = useState('');
  /** The one moment the key exists outside the caller's clipboard. */
  const [issued, setIssued] = useState<{ name: string; key: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const keys = useQuery({
    queryKey: ['inventory', 'keys'],
    queryFn: () => api.get<{ items: KeyRow[] }>('/inventory/keys'),
    enabled: isManager,
  });

  const create = useMutation({
    mutationFn: (input: { name: string }) =>
      api.post<{ name: string; key: string }>('/inventory/keys', input),
    onSuccess: (result) => {
      setName('');
      setCopied(false);
      setIssued(result);
      void queryClient.invalidateQueries({ queryKey: ['inventory', 'keys'] });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: number) => api.delete(`/inventory/keys/${String(id)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory', 'keys'] });
    },
  });

  const createErrors = errorsOf(create.error);

  return (
    <>
      {isManager ? (
        <Section
          title="API keys"
          description="A key lets a script do what this page does. It is shown once and stored only as a hash — if you lose it, revoke it and make another."
        >
          {issued ? (
            <div className="mb-5 rounded-lg border border-emerald-300 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/50">
              <p className="text-sm font-medium text-emerald-900 dark:text-emerald-100">
                “{issued.name}” is ready. Copy it now — this is the only time it is shown.
              </p>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <code className="lg-scroll min-w-0 flex-1 overflow-x-auto rounded-md bg-white px-3 py-2 font-mono text-xs whitespace-nowrap text-slate-800 dark:bg-slate-900 dark:text-slate-100">
                  {issued.key}
                </code>

                <button
                  type="button"
                  className="lg-btn-secondary"
                  onClick={() => {
                    void navigator.clipboard.writeText(issued.key).then(() => {
                      setCopied(true);
                    });
                  }}
                >
                  {copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardIcon className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>

                <button
                  type="button"
                  className="lg-btn-ghost"
                  onClick={() => {
                    setIssued(null);
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          ) : null}

          {/* The button belongs on the input's own line. Wrapping the input in
              a Field and aligning to the bottom of that put the button level
              with the hint underneath instead, which reads as a misalignment
              because it is one. */}
          <form
            className="mb-5"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate({ name });
            }}
          >
            <label className="lg-label" htmlFor="key-name">
              What is it for?
            </label>

            <div className="flex flex-wrap gap-2">
              <input
                id="key-name"
                className="lg-input min-w-0 flex-1"
                placeholder="Nightly feed"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />

              <button
                type="submit"
                className="lg-btn-primary shrink-0"
                disabled={create.isPending || !name.trim()}
              >
                {create.isPending ? <Spinner /> : <PlusIcon className="h-4 w-4" />}
                Create key
              </button>
            </div>

            {createErrors.fieldErrors.name ?? createErrors.message ? (
              <p className="mt-1 text-xs text-rose-600 dark:text-rose-400" role="alert">
                {createErrors.fieldErrors.name ?? createErrors.message}
              </p>
            ) : (
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                A name you will recognise in six months — “nightly feed”, “stock-take laptop”.
              </p>
            )}
          </form>

          {keys.isLoading ? (
            <Spinner className="h-5 w-5 text-brand-600" />
          ) : (keys.data?.items.length ?? 0) === 0 ? (
            <EmptyState
              title="No keys yet"
              description="Create one to fill this inventory from a feed, a spreadsheet script or your own back office."
            />
          ) : (
            <ul className="divide-y divide-slate-200 dark:divide-slate-800">
              {(keys.data?.items ?? []).map((key) => (
                <li key={key.id} className="flex items-center gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-50">
                      {key.name}
                      <span className="ml-2 font-mono text-xs font-normal text-slate-400">
                        {key.prefix}…
                      </span>
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                      {key.createdBy ? `${key.createdBy} · ` : ''}
                      created {formatRelative(key.createdAt)} ·{' '}
                      {key.lastUsedAt ? `last used ${formatRelative(key.lastUsedAt)}` : 'never used'}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="lg-btn-ghost px-2 hover:text-rose-600"
                    onClick={() => {
                      void confirm({
                        title: `Revoke “${key.name}”?`,
                        message:
                          'Anything still using it stops working immediately. This cannot be undone — issue a new key instead.',
                        confirmLabel: 'Revoke',
                        destructive: true,
                      }).then((ok) => {
                        if (ok) revoke.mutate(key.id);
                      });
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span className="sr-only">Revoke</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Section>
      ) : null}

      <Section
        title="Using the API"
        description="Built from this workspace’s own fields, so the examples are ready to paste."
      >
        <ApiDocs fields={fields} settings={settings} />
      </Section>
    </>
  );
}

// --- standing requests ------------------------------------------------------

function RequestsTab(): React.JSX.Element {
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const rules = useQuery({
    queryKey: ['inventory', 'rules'],
    queryFn: () => api.get<{ items: RuleRow[] }>('/inventory/rules'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/inventory/rules/${String(id)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory', 'rules'] });
    },
  });

  return (
    <Section
      title="Standing requests"
      description="Customers waiting on stock that has not arrived yet. Each one is notified once per matching item."
    >
      {rules.isLoading ? (
        <Spinner className="h-5 w-5 text-brand-600" />
      ) : (rules.data?.items.length ?? 0) === 0 ? (
        <EmptyState
          title="No standing requests"
          description="When a customer asks for something you do not have, record it here and they will hear from you automatically."
        />
      ) : (
        <ul className="divide-y divide-slate-200 dark:divide-slate-800">
          {(rules.data?.items ?? []).map((rule) => (
            <li key={rule.id} className="flex items-start gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-50">
                  {rule.criteria.length === 0
                    ? 'Anything new'
                    : rule.criteria
                        .map(
                          (criterion) =>
                            `${criterion.label} ${criterion.op === 'contains' ? 'contains' : 'is'} ${criterion.value}`,
                        )
                        .join(' · ')}
                </p>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {rule.customerName} · {rule.customerEmail ?? rule.customerPhone ?? 'no contact'} ·
                  requested {formatRelative(rule.createdAt)}
                </p>
              </div>

              {rule.notificationCount > 0 ? (
                <span className="lg-badge bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200">
                  {rule.notificationCount} sent
                </span>
              ) : null}

              {rule.unsubscribed ? (
                <span className="lg-badge bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  Opted out
                </span>
              ) : null}

              <button
                type="button"
                className="lg-btn-ghost px-2 hover:text-rose-600"
                onClick={() => {
                  void confirm({
                    title: 'Delete this request?',
                    message: 'The customer will no longer be told when matching stock arrives.',
                    confirmLabel: 'Delete',
                    destructive: true,
                  }).then((ok) => {
                    if (ok) remove.mutate(rule.id);
                  });
                }}
              >
                <TrashIcon className="h-4 w-4" />
                <span className="sr-only">Delete</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
